import { LGraphNode, LiteGraph } from "litegraph.js";
import { addDrivableSlider, finalizeDrivableSliders, syncDrivableSliders } from "../utils/addDrivableSlider.js";
import { WIND_DATA, windVectorMatrix, windBounce } from "../engine/drivers.js";

/**
 * WindNode
 * --------
 * Cycles through wind data from cities around the world.
 * Converts wind speed + direction into directional bounce values
 * that drive BounceNode's Offset X / Offset Y.
 *
 * Uses the same animateElement() and windVectorMatrix() logic as
 * effects.js so the behaviour matches the embeddable headers.
 *
 * Accepts a Time input (0→1 from TimingFunctionNode) so the bounce
 * stays in sync with the grid animation.
 */

function degreesToCompass(deg) {
  const dirs = ["N","NE","E","SE","S","SW","W","NW"];
  return dirs[Math.round(deg / 45) % 8];
}

// ─── Node ────────────────────────────────────────────────────────────────────

export class WindNode extends LGraphNode {
  constructor() {
    super();
    this.title = "Wind Data";

    this.properties = {
      cycleDuration: 6,   // seconds per city
      chillDuration: 3,   // seconds of calm between cities
      intensity: 1,       // multiplier on wind speed
      exaggeration: 1.7,  // exponent for wind speed curve
    };

    this._currentIndex = 0;
    this._lastSwitch = performance.now();
    this._inChill = false;
    this._phase = 0; // fallback internal timer when no Time input
    this._currentMatrix = { xMin: 0, xMax: 0, yMin: 0, yMax: 0 };
    this._targetMatrix  = { xMin: 0, xMax: 0, yMin: 0, yMax: 0 };
    this._currentWind = WIND_DATA[0];

    // ── Inputs ──────────────────────────────────────────────
    // Time input (optional): connect TimingFunctionNode here so bounce
    // syncs with the grid animation. If unconnected, uses internal timer.
    this.addInput("Time", "number");

    // ── Outputs ─────────────────────────────────────────────
    this.addOutput("Bounce X", "number");
    this.addOutput("Bounce Y", "number");
    this.addOutput("Wind Speed", "number");
    this.addOutput("Direction", "number");

    // ── Widgets ─────────────────────────────────────────────
    this.addWidget("slider", "Cycle (s)", this.properties.cycleDuration, (val) => {
      this.properties.cycleDuration = Math.round(val * 10) / 10;
    }, { min: 1, max: 20, step: 0.5 });

    // Drivable sliders — can be driven by oscillators etc.
    addDrivableSlider(this, "Intensity", "intensity", this.properties.intensity, 0, 5, 0.1);
    addDrivableSlider(this, "Exaggeration", "exaggeration", this.properties.exaggeration, 0.5, 4, 0.1);

    // 1 visible input (Time), rest are drivable hidden inputs
    finalizeDrivableSliders(this, 1);

    this.size = [200, 210];
    this.mode = LiteGraph.ALWAYS;
    this.color = "#2a2a2a";
    this.bgcolor = "#333333";
  }

  onDrawForeground(ctx) {
    const w = this.size[0];
    const h = this.size[1];
    const wind = this._currentWind;
    const y = h - 50;

    // City name
    ctx.fillStyle = "rgba(255,255,255,0.8)";
    ctx.font = "bold 11px sans-serif";
    ctx.fillText(`${wind.city}, ${wind.country}`, 10, y);

    // Wind info
    ctx.fillStyle = "rgba(255,255,255,0.5)";
    ctx.font = "10px sans-serif";
    const compass = degreesToCompass(wind.wind_direction);
    ctx.fillText(`${compass}  ${wind.wind_speed} m/s`, 10, y + 14);

    // Wind direction arrow
    const cx = w - 30;
    const cy = y + 5;
    const r = 15;
    const angle = ((wind.wind_direction + 90) * Math.PI) / 180;

    ctx.strokeStyle = "rgba(255,255,255,0.3)";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.stroke();

    ctx.strokeStyle = "#4E9EFF";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(cx, cy);
    const tipX = cx + Math.cos(angle) * r;
    const tipY = cy + Math.sin(angle) * r;
    ctx.lineTo(tipX, tipY);
    ctx.stroke();

    ctx.fillStyle = "#4E9EFF";
    ctx.beginPath();
    ctx.arc(tipX, tipY, 3, 0, Math.PI * 2);
    ctx.fill();

    if (this._inChill) {
      ctx.fillStyle = "rgba(255,255,255,0.3)";
      ctx.font = "9px sans-serif";
      ctx.fillText("calm...", 10, y + 28);
    }
  }

  onExecute() {
    syncDrivableSliders(this);

    const now = performance.now();
    const elapsed = (now - this._lastSwitch) / 1000;

    // ── City cycling ────────────────────────────────────────
    if (this._inChill) {
      // Ease toward neutral (zero displacement)
      this._currentMatrix.xMin += (0 - this._currentMatrix.xMin) * 0.05;
      this._currentMatrix.xMax += (0 - this._currentMatrix.xMax) * 0.05;
      this._currentMatrix.yMin += (0 - this._currentMatrix.yMin) * 0.05;
      this._currentMatrix.yMax += (0 - this._currentMatrix.yMax) * 0.05;

      if (elapsed >= this.properties.chillDuration) {
        this._inChill = false;
        this._lastSwitch = now;
        this._currentIndex = (this._currentIndex + 1) % WIND_DATA.length;
        this._currentWind = WIND_DATA[this._currentIndex];
      }
    } else {
      // Recalculate target each frame so live-driven intensity/exaggeration works
      const dir = this._currentWind.wind_direction + 90;
      this._targetMatrix = windVectorMatrix(
        this._currentWind.wind_speed * this.properties.intensity,
        dir,
        this.properties.exaggeration
      );

      // Ease toward target
      this._currentMatrix.xMin += (this._targetMatrix.xMin - this._currentMatrix.xMin) * 0.08;
      this._currentMatrix.xMax += (this._targetMatrix.xMax - this._currentMatrix.xMax) * 0.08;
      this._currentMatrix.yMin += (this._targetMatrix.yMin - this._currentMatrix.yMin) * 0.08;
      this._currentMatrix.yMax += (this._targetMatrix.yMax - this._currentMatrix.yMax) * 0.08;

      if (elapsed >= this.properties.cycleDuration) {
        this._inChill = true;
        this._lastSwitch = now;
      }
    }

    // ── Bounce output — uses animateElement() identical to effects.js ──
    // If Time input is connected, use it (syncs with grid animation).
    // Otherwise use internal phase counter.
    let t;
    const timeInput = this.getInputData(0);
    if (timeInput != null) {
      t = timeInput;
    } else {
      this._phase += 1 / 60; // ~1 cycle per second at 60fps
      if (this._phase > 1000) this._phase -= 1000;
      t = this._phase % 1;
    }

    const m = this._currentMatrix;
    const bx = windBounce(t, m.xMin, m.xMax);
    const by = windBounce(t, m.yMin, m.yMax);

    this.setOutputData(0, bx);
    this.setOutputData(1, by);
    this.setOutputData(2, this._currentWind.wind_speed);
    this.setOutputData(3, this._currentWind.wind_direction);
    this.setDirtyCanvas(true);
  }
}

LiteGraph.registerNodeType("generators/WindData", WindNode);
